/**
 * Family Hooks
 *
 * React Query hooks for family management operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import {
  createFamilyService,
  type CreateFamilyMemberInput,
  type FamilyInviteInput,
  type FamilyMember,
  type UpdateFamilyMemberInput,
  type UploadFamilyDocumentInput,
} from '../services/family.service';

// Query keys
export const familyKeys = {
  all: ['family'] as const,
  members: () => [...familyKeys.all, 'members'] as const,
  member: (id: string) => [...familyKeys.all, 'member', id] as const,
  documents: (memberId: string) => [...familyKeys.all, 'documents', memberId] as const,
};

/**
 * Get all family members
 */
export function useFamilyMembers(options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const familyService = createFamilyService(client);

  return useQuery({
    queryKey: familyKeys.members(),
    queryFn: () => familyService.getMembers(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Get family member by ID
 */
export function useFamilyMember(id: string) {
  const { client } = useSdkContext();
  const familyService = createFamilyService(client);

  return useQuery({
    queryKey: familyKeys.member(id),
    queryFn: () => familyService.getMember(id),
    enabled: !!id,
  });
}

/**
 * Add a new family member
 */
export function useAddFamilyMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const familyService = createFamilyService(client);

  return useMutation({
    mutationFn: (input: CreateFamilyMemberInput) => familyService.addMember(input),
    onSuccess: (newMember) => {
      // Add to members list cache
      queryClient.setQueryData<FamilyMember[]>(familyKeys.members(), (old) =>
        old ? [...old, newMember] : [newMember]
      );
    },
  });
}

/**
 * Update a family member
 */
export function useUpdateFamilyMember(id: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const familyService = createFamilyService(client);

  return useMutation({
    mutationFn: (input: UpdateFamilyMemberInput) => familyService.updateMember(id, input),
    onSuccess: (updatedMember) => {
      // Update member in cache
      queryClient.setQueryData(familyKeys.member(id), updatedMember);
      // Update in members list
      queryClient.setQueryData<FamilyMember[]>(familyKeys.members(), (old) =>
        old ? old.map((m) => (m.id === id ? updatedMember : m)) : [updatedMember]
      );
    },
  });
}

/**
 * Remove a family member
 */
export function useRemoveFamilyMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const familyService = createFamilyService(client);

  return useMutation({
    mutationFn: (id: string) => familyService.removeMember(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: familyKeys.member(id) });
      // Remove from members list
      queryClient.setQueryData<FamilyMember[]>(familyKeys.members(), (old) =>
        old ? old.filter((m) => m.id !== id) : []
      );
    },
  });
}

/**
 * Get documents for a family member
 */
export function useFamilyMemberDocuments(memberId: string) {
  const { client } = useSdkContext();
  const familyService = createFamilyService(client);

  return useQuery({
    queryKey: familyKeys.documents(memberId),
    queryFn: () => familyService.getMemberDocuments(memberId),
    enabled: !!memberId,
  });
}

/**
 * Upload document for a family member
 */
export function useUploadFamilyDocument() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const familyService = createFamilyService(client);

  return useMutation({
    mutationFn: (input: UploadFamilyDocumentInput) => familyService.uploadDocument(input),
    onSuccess: (_newDocument, input) => {
      // Invalidate documents cache to refetch
      queryClient.invalidateQueries({ queryKey: familyKeys.documents(input.memberId) });
    },
  });
}

/**
 * Invite a family member to join
 */
export function useInviteFamilyMember() {
  const { client } = useSdkContext();
  const familyService = createFamilyService(client);

  return useMutation({
    mutationFn: (input: FamilyInviteInput) => familyService.inviteMember(input),
  });
}
